import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, Car, Wrench, Clock, CheckCircle, Circle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  received: 'bg-blue-500',
  diagnostic: 'bg-yellow-500',
  in_progress: 'bg-orange-500',
  quality_check: 'bg-purple-500',
  ready: 'bg-green-500'
};

const statusLabels = {
  received: 'Réceptionné',
  diagnostic: 'Diagnostic',
  in_progress: 'En cours',
  quality_check: 'Contrôle qualité',
  ready: 'Prêt'
};

export default function TrackingPage() {
  const [plate, setPlate] = useState('');
  const [loading, setLoading] = useState(false);
  const [repairs, setRepairs] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!plate.trim()) return;
    
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/repairs/track/${plate.trim().toUpperCase()}`);
      setRepairs(res.data);
      if (res.data.length === 0) {
        toast.info('Aucune réparation trouvée pour ce véhicule');
      }
    } catch (error) {
      toast.error('Erreur lors de la recherche');
      setRepairs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="text-red-500" size={32} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            SUIVI DE <span className="text-red-500">RÉPARATION</span>
          </h1>
          <p className="text-zinc-400">Suivez l'avancement de votre véhicule en temps réel</p>
        </div>

        {/* Search Form */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1 relative">
                <Car className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <Input
                  placeholder="Entrez votre plaque d'immatriculation (ex: AB-123-CD)"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  className="bg-zinc-950 border-zinc-800 text-white pl-12 h-14 text-lg"
                  data-testid="tracking-plate-input"
                />
              </div>
              <Button 
                type="submit" 
                className="bg-red-600 hover:bg-red-700 h-14 px-8"
                disabled={loading}
                data-testid="tracking-search-btn"
              >
                {loading ? 'Recherche...' : (
                  <>
                    <Search className="mr-2" size={18} />
                    Rechercher
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && repairs.length === 0 && !loading && (
          <Card className="bg-zinc-900/50 border-zinc-800 text-center">
            <CardContent className="py-12">
              <Car className="mx-auto text-zinc-600 mb-4" size={48} />
              <h3 className="text-xl text-white mb-2">Aucune réparation trouvée</h3>
              <p className="text-zinc-500">
                Vérifiez votre numéro d'immatriculation ou contactez-nous.
              </p>
            </CardContent>
          </Card>
        )}

        {repairs.length > 0 && (
          <div className="space-y-6">
            {repairs.map((repair) => (
              <Card key={repair.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`repair-card-${repair.id}`}>
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">
                          {repair.vehicle_brand} {repair.vehicle_model}
                        </h3>
                        <Badge className={`${statusColors[repair.status]} text-white`}>
                          {statusLabels[repair.status]}
                        </Badge>
                      </div>
                      <p className="text-zinc-500">
                        <span className="font-mono">{repair.vehicle_plate}</span>
                        <span className="mx-2">•</span>
                        {repair.service_type}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-500 text-sm">Progression</p>
                      <p className="text-3xl font-bold text-red-500">{repair.progress}%</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-zinc-800 rounded-full h-3 mb-6">
                    <div 
                      className="bg-red-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${repair.progress}%` }}
                    />
                  </div>

                  {/* Steps */}
                  <div className="relative">
                    <div className="absolute top-4 left-4 right-4 h-0.5 bg-zinc-800" />
                    <div className="flex justify-between relative">
                      {repair.steps.map((step, idx) => (
                        <div key={idx} className="flex flex-col items-center text-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                            step.completed 
                              ? 'bg-green-500 text-white' 
                              : 'bg-zinc-800 text-zinc-500'
                          }`}>
                            {step.completed ? <CheckCircle size={16} /> : <Circle size={16} />}
                          </div>
                          <span className={`mt-2 text-xs ${step.completed ? 'text-green-400' : 'text-zinc-500'}`}>
                            {step.name}
                          </span>
                          {step.date && (
                            <span className="text-[10px] text-zinc-600">
                              {new Date(step.date).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-6 pt-6 border-t border-zinc-800">
                    <p className="text-zinc-400">{repair.description}</p>
                    {repair.estimated_completion && (
                      <p className="text-zinc-500 mt-2 flex items-center gap-2">
                        <Clock size={16} />
                        Fin estimée: {new Date(repair.estimated_completion).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-12 text-center">
          <p className="text-zinc-500">
            Pour toute question sur votre véhicule, contactez-nous au{' '}
            <a href="tel:0805032032" className="text-red-500 hover:underline">0805 032 032</a>
          </p>
        </div>
      </div>
    </div>
  );
}
